import React, { useState, useEffect, useMemo } from 'react';
import styles from '../Patient.module.css';
import { LeftVector, RightVector, TaskTypeBGIcon } from '../../../components/icons/Icons';
import {
    add,
    eachDayOfInterval,
    endOfMonth,
    endOfWeek,
    format,
    getDay,
    isSameMonth,
    isSameDay,
    isToday,
    parse,
    isAfter,
    startOfToday,
    startOfWeek,
    parseISO,
} from "date-fns";
import { useSelector } from 'react-redux';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCalendarAlt, faHeartPulse, faBell, faCircle, faQuoteLeft, faArrowRightFromBracket, faListCheck, faClipboardCheck } from '@fortawesome/free-solid-svg-icons';

const PopupCalendarView = () => {

    const {appointments, patientNotifications, tasksByPatient} = useSelector((state) => state.patient);

    const iconMapping = (item) => {
        switch (item.type) {
            case 'Appointment':
                return <FontAwesomeIcon icon={faCalendarAlt} />;
            case 'Bell':
                return <FontAwesomeIcon icon={faBell} />;
            case 'Task':
                if (item.description.toLowerCase().includes("transcription")) {
                    return <FontAwesomeIcon icon={faQuoteLeft} />;
                } else if (item.description.toLowerCase().includes("enrollment")) {
                    return <FontAwesomeIcon icon={faArrowRightFromBracket} />;
                } else if (item.description.toLowerCase().includes("vitals")) {
                    return <FontAwesomeIcon icon={faHeartPulse} />;
                } else if (item.description.toLowerCase().includes("report")) {
                    return <FontAwesomeIcon icon={faClipboardCheck} />;
                } else {return <FontAwesomeIcon icon={faListCheck} />;}
            default:
            return <FontAwesomeIcon icon={faCircle} />;
        }
    };


    const today = startOfToday();
    const days = ["sun", "mon", "tue", "wed", "thu", "fri", "sat"];
    const colStartClasses = [
        "",
        "col-start-2",
        "col-start-3",
        "col-start-4",
        "col-start-5",
        "col-start-6",
        "col-start-7",
    ];
    const [selectedDay, setSelectedDay] = useState(new Date());
    const [currMonth, setCurrMonth] = useState(() => format(today, "MMM-yyyy"));
    const [firstDayOfMonth, setFirstDayOfMonth] = useState(parse(currMonth, "MMM-yyyy", new Date()));
    useEffect(() => {
        setFirstDayOfMonth(parse(currMonth, "MMM-yyyy", new Date()));
    }, [currMonth]);

    const daysInMonth = eachDayOfInterval({
        start: startOfWeek(firstDayOfMonth),
        end: endOfWeek(endOfMonth(firstDayOfMonth)),
    });

    const getPrevMonth = () => {
        const firstDayOfPrevMonth = add(firstDayOfMonth, { months: -1 });
        setCurrMonth(format(firstDayOfPrevMonth, "MMM-yyyy"));
    };

    const getNextMonth = () => {
        const firstDayOfNextMonth = add(firstDayOfMonth, { months: 1 });
        setCurrMonth(format(firstDayOfNextMonth, "MMM-yyyy"));
    };

    const formatApptDate = (dateString) => {
        const date = parseISO(dateString);
        return `${format(date, "MMMM d, yyyy")}   |   ${format(date, "hh:mm a")}`;
    };


    const handleDayClick = (day) => {
        setSelectedDay(day);
    };
    
    const sortedEvents = useMemo(() => {
        if (selectedDay) {
            const apptMatches = appointments.filter((appt) => {
                const apptDate = parseISO(appt.appointmentStartDateTime);
                return isSameDay(apptDate, selectedDay);
            });
            const taskMatches = tasksByPatient.filter((task) => {
                const taskDate = task.dueDate ? parseISO(task.dueDate) : parseISO(task.createdDate);
                return isSameDay(taskDate, selectedDay);
            });
            const notificationMatches = patientNotifications.filter((notification) => {
                const notificationDate = parseISO(notification.createdDate);
                return isSameDay(notificationDate, selectedDay);
            });
            return [...apptMatches, ...taskMatches, ...notificationMatches];
        } else {
            return [
                ...appointments.filter((appt) => isAfter(parseISO(appt.appointmentStartDateTime), today)),
                ...tasksByPatient.filter((task) => {
                    const taskDate = task.dueDate ? parseISO(task.dueDate) : parseISO(task.createdDate);
                    return isAfter(taskDate, today);
                }),
                ...patientNotifications.filter((notification) => isAfter(parseISO(notification.createdDate), today)),
            ];
        }
    }, [selectedDay, appointments, tasksByPatient, patientNotifications, today]);

    const getOrdinal = (day) => {
        const suffixes = ["th", "st", "nd", "rd"];
        const value = day % 100;
        return suffixes[(value - 20) % 10] || suffixes[value] || suffixes[0];
    };

    const formattedDate = (date) => {
        const currentDate = date ? new Date(date) : new Date();
        const day = format(currentDate, "d");
        return `${format(currentDate, "MMMM d")}${getOrdinal(day)} - ${format(currentDate, "yyyy")}`;
    };

    const selectedDate = formattedDate(selectedDay);

    const checkEventsOnSelectedDate = (day) => {
        const apptMatches = appointments.filter((appt) => isSameDay(parseISO(appt.appointmentStartDateTime), day));
        const taskMatches = tasksByPatient.filter((task) => {
            const taskDate = task.dueDate ? parseISO(task.dueDate) : parseISO(task.createdDate);
            return isSameDay(taskDate, day);
        });
        const notificationMatches = patientNotifications.filter((notification) => isSameDay(parseISO(notification.createdDate), day));

        return apptMatches.length > 0 || taskMatches.length > 0 || notificationMatches.length > 0;
    };

    const stripHtmlTags = (input) => input.replace(/<\/?[^>]+(>|$)/g, "");



    return (
        <div style={{ width: "100%", height: "591px", padding: "10px", display: "flex", flexDirection: "column", gap: "10px" }}>
            <div className={styles.popupCalendarContainer}>
                <div className={styles.calendarHeader}>
                    <div className="body-1">{format(firstDayOfMonth, "MMMM yyyy")}</div>
                    <div className={styles.calendarNavigation}>
                        <button onClick={() => getPrevMonth()}><LeftVector /></button>
                        <button onClick={() => getNextMonth()}><RightVector /></button>
                    </div>
                </div>
                <div className={styles.dayOfWeekContainer}>
                    {days.map((day, idx) => (
                        <div key={idx} className={styles.dayLabel}>{day}</div>
                    ))}
                </div>
                <div className={styles.daysContainer}>
                    {daysInMonth.map((day, idx) => (
                        <div key={idx} className={`${styles.day} ${styles[colStartClasses[getDay(day)]]}`} onClick={() => handleDayClick(day)}>
                            <div className={`${styles.dayNumber} ${isSameMonth(day, firstDayOfMonth) ? styles.currentMonth : styles.otherMonth} ${isToday(day) ? styles.today : isSameDay(day, selectedDay) ? styles.selectedDay : styles.notToday}`}>
                                {format(day, "d")}
                                <div className={styles.calendarIcons}>
                                    {checkEventsOnSelectedDate(day) && <div className={styles.eventDot} />}
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            </div>

            <div className={styles.CalendarAlertsTitle}>
                Alerts & Events
                <span style={{ display: 'inline-block', height: '1.5em', width: '.5px', backgroundColor: '#666', margin: '0 8px' }} />
                <span style={{ fontSize: '0.75em' }}>{selectedDate}</span>
                <span style={{ display: 'inline-block', height: '1em', width: '.5px', backgroundColor: '#666', margin: '0 8px' }} />
                <span style={{ fontSize: '0.75em', paddingLeft: '5px', paddingRight: '5px' }}>{sortedEvents?.length ?? 0} items</span>
            </div>

            <div style={{ height: "100%", overflowY: "scroll" }}>
                {sortedEvents.map((item, index) => (
                    <div key={index} className={styles.AlertContainer}>

                        {iconMapping(item)}
                        <div>
                            <span className="body-1">
                                {item.type === "Appointment" ? `Appointment with ${item.physicianName !== "" ? `${item.physicianName}` : "physician outside of provider's care team"}`
                                    : item.dueDate
                                        ? `Reminder: ${stripHtmlTags(item.description)}`
                                        : stripHtmlTags(item.description || item.notificationContent)}
                            </span>
                            <div style={{ display: "flex", columnGap: "10px" }}>
                                <span className="caption" style={{ paddingRight: "10px" }}>
                                    {formatApptDate(item.appointmentStartDateTime || item.dueDate || item.createdDate)}
                                </span>
                            </div>
                        </div>
                    </div>
                ))}
            </div>

        </div>
    );
};

export default PopupCalendarView;
